import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format, subYears } from 'date-fns';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import AnalysisCandlestickChart from '../components/AnalysisCandlestickChart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const SubScore = ({ value }) => (
  <div className="flex items-center gap-2 min-w-[90px]">
    <div className="h-1.5 flex-1 rounded-full bg-[#E6DCCF] overflow-hidden">
      <div className="h-full rounded-full bg-[#C86F4A]" style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
    </div>
    <span className="text-xs text-[#7A6A5C] tabular-nums w-8 text-right">%{Math.round(value)}</span>
  </div>
);

const Pct = ({ value, bold = false }) => {
  if (value === null || value === undefined) return <span className="text-[#7A6A5C]">—</span>;
  const color = value > 0 ? 'text-[#6D7C3B]' : value < 0 ? 'text-[#B04832]' : 'text-[#7A6A5C]';
  return (
    <span className={`tabular-nums ${color} ${bold ? 'font-semibold' : ''}`}>
      {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
};

const subScoreText = (row) => {
  const parts = [];
  const tag = (v) => (v >= 80 ? 'güçlü' : v >= 60 ? 'olumlu' : v <= 20 ? 'zayıf' : v <= 40 ? 'olumsuz' : 'nötr');
  parts.push(`Mal toplama ${tag(row.acc_pct)}`);
  parts.push(`geometri ${tag(row.geo_pct)}`);
  parts.push(`topoloji ${tag(row.top_pct)}`);
  return parts.join(', ');
};

// Satır açılınca: son 1 yılın günlük grafiği + alt skorların sözlü özeti
const StockDetail = ({ row, sinceDate, onOpenAnalysis }) => {
  const { getAuthHeader } = useAuth();
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const end = new Date();
    axios
      .get(`${API_URL}/stocks/${row.symbol}/candlestick`, {
        params: { interval: '1d', start_date: format(subYears(end, 1), 'yyyy-MM-dd'), end_date: format(end, 'yyyy-MM-dd') },
        headers: getAuthHeader(),
      })
      .then((res) => alive && setCandles(res.data?.candles || []))
      .catch(() => alive && toast.error(`${row.symbol} grafiği yüklenemedi`))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.symbol]);

  const today = format(new Date(), 'yyyy-MM-dd');
  return (
    <div className="px-4 py-4 bg-[#FBF8F4] border-b border-[#E6DCCF] space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#7A6A5C] leading-relaxed">
          <span className="font-semibold text-[#2E2620]">{row.symbol}</span> · {subScoreText(row)}.
          {sinceDate && (
            <> Mavi bölge, listeye girdiği <span className="font-medium text-[#2E2620]">{sinceDate}</span> gününden bugüne.</>
          )}
        </p>
        <Button size="sm" variant="outline" onClick={() => onOpenAnalysis(row.symbol)}
          className="border-[#2E2620]/20 text-[#2E2620] rounded-full">
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Benzerlik analizinde aç
        </Button>
      </div>
      <div className="rounded-xl overflow-hidden border border-[#E6DCCF] bg-white">
        <AnalysisCandlestickChart
          data={candles}
          loading={loading}
          height={300}
          highlightStartDate={sinceDate || null}
          highlightEndDate={sinceDate ? today : null}
        />
      </div>
    </div>
  );
};

const ROLE_LABELS = {
  champion: { text: 'Ana model', cls: 'bg-[#6D7C3B]/15 text-[#6D7C3B] border-[#6D7C3B]/30' },
  candidate: { text: 'Aday (gölge)', cls: 'bg-[#C86F4A]/15 text-[#C86F4A] border-[#C86F4A]/30' },
  retired: { text: 'Emekli', cls: 'bg-[#E6DCCF]/60 text-[#7A6A5C] border-[#E6DCCF]' },
  rejected: { text: 'Reddedildi', cls: 'bg-[#B04832]/10 text-[#B04832] border-[#B04832]/30' },
};

const SymbolChips = ({ title, items, tone }) => (
  <div>
    <p className="text-xs font-medium text-[#2E2620] mb-1.5">{title} <span className="text-[#7A6A5C]">({items.length})</span></p>
    <div className="flex flex-wrap gap-1.5">
      {items.length ? items.map((s) => (
        <span key={s} className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>{s}</span>
      )) : <span className="text-xs text-[#7A6A5C]">—</span>}
    </div>
  </div>
);

const ComparePanel = () => {
  const { getAuthHeader } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/simons/compare`, { headers: getAuthHeader() });
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Karşılaştırma yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (action) => {
    const text = action === 'promote'
      ? 'Aday model ana model olacak; yarın sabahtan itibaren listeler onunla hesaplanır. Emin misiniz?'
      : 'Aday model kaldırılacak. Emin misiniz?';
    if (!window.confirm(text)) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/simons/candidate/${action}`, {}, { headers: getAuthHeader() });
      toast.success(action === 'promote' ? 'Aday ana model yapıldı' : 'Aday kaldırıldı');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'İşlem yapılamadı');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !state) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#C86F4A]" /></div>;
  }
  if (!state) return null;
  const { champion, candidate, live, overlap, history, min_weeks: minWeeks, can_decide: canDecide } = state;
  const ic = (v) => (typeof v === 'number' ? v.toFixed(3) : '—');
  const rate = (v) => (typeof v === 'number' ? `%${Math.round(v)}` : '—');

  return (
    <div className="space-y-4">
      <Card className="card-organic">
        <CardContent className="pt-6 text-sm text-[#7A6A5C] leading-relaxed space-y-2">
          <p>
            Ekrandaki liste her zaman <span className="font-medium text-[#2E2620]">ana modelden</span> gelir. Modelde yapısal bir
            değişiklik denendiğinde yeni sürüm <span className="font-medium text-[#2E2620]">aday</span> olarak gölgede çalışır: her
            sabah o da skorlanır, listesi sinyal defterine ayrıca kaydedilir. Aynı haftalardaki gerçek sonuçlar yan yana
            karşılaştırılır; en az {minWeeks} haftalık sonuç birikince terfi kararı verilebilir.
          </p>
          <p>
            Aylık yeniden eğitim (yalnızca bir ay daha veri) gölgeye alınmaz; ama güvenlik kontrolünden geçer: doğrulama skoru
            ana modelinkinin yarısının altındaysa devreye girmez, eski model çalışmaya devam eder.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="card-organic"><CardContent className="pt-5 space-y-1">
          <Badge className={ROLE_LABELS.champion.cls}>Ana model</Badge>
          <p className="text-sm font-semibold text-[#2E2620] mt-2">{champion?.version || '—'}</p>
          <p className="text-xs text-[#7A6A5C]">
            {champion ? `Eğitim ${new Date(champion.trained_at).toLocaleDateString('tr-TR')} · doğrulama IC ${ic(champion.holdout_ic)}` : 'Model yok'}
          </p>
        </CardContent></Card>
        <Card className="card-organic"><CardContent className="pt-5 space-y-1">
          <Badge className={ROLE_LABELS.candidate.cls}>Aday (gölge)</Badge>
          {candidate ? (
            <>
              <p className="text-sm font-semibold text-[#2E2620] mt-2">{candidate.version}</p>
              <p className="text-xs text-[#7A6A5C]">
                Eğitim {new Date(candidate.trained_at).toLocaleDateString('tr-TR')} · doğrulama IC {ic(candidate.holdout_ic)}
              </p>
            </>
          ) : (
            <p className="text-sm text-[#7A6A5C] mt-2">Şu an deneme aşamasında aday model yok; tek model çalışıyor.</p>
          )}
        </CardContent></Card>
      </div>

      {candidate && (
        <Card className="card-organic overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#2E2620]">Gerçek sonuçlar (aynı haftalar, 4 hafta sonra)</CardTitle>
            <p className="text-xs text-[#7A6A5C]">
              {live?.weeks || 0} / {minWeeks} hafta ölçüldü{live?.weeks < minWeeks ? ' · karar için yeterli değil' : ''}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {live?.champion && live?.candidate ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                    <th className="text-left px-4 py-2.5 font-medium"></th>
                    <th className="text-right px-4 py-2.5 font-medium">Ana model</th>
                    <th className="text-right px-4 py-2.5 font-medium">Aday</th>
                  </tr>
                </thead>
                <tbody className="text-[#2E2620]">
                  <tr className="border-b border-[#E6DCCF]"><td className="px-4 py-2.5">En yüksek 30 piyasayı geçme oranı <span className="text-xs text-[#7A6A5C]">(yüksek iyi)</span></td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{rate(live.champion.top_beat_rate)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{rate(live.candidate.top_beat_rate)}</td></tr>
                  <tr className="border-b border-[#E6DCCF]"><td className="px-4 py-2.5">En düşük 30 piyasayı geçme oranı <span className="text-xs text-[#7A6A5C]">(düşük iyi)</span></td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{rate(live.champion.bottom_beat_rate)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{rate(live.candidate.bottom_beat_rate)}</td></tr>
                  <tr className="border-b border-[#E6DCCF]"><td className="px-4 py-2.5">İlk grubun ikinciden iyi gittiği hafta</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{live.champion.positive_weeks}/{live.weeks}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{live.candidate.positive_weeks}/{live.weeks}</td></tr>
                  <tr><td className="px-4 py-2.5">İki grup arası ortalama fark (puan)</td>
                    <td className="px-4 py-2.5 text-right tabular-nums"><Pct value={live.champion.mean_spread} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums"><Pct value={live.candidate.mean_spread} /></td></tr>
                </tbody>
              </table>
            ) : (
              <p className="px-6 py-5 text-sm text-[#7A6A5C]">
                Henüz sonuç yok. Aday her hafta kaydediliyor; ilk sonuçlar kayıttan 4 hafta sonra gelir.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {candidate && overlap && (
        <Card className="card-organic">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#2E2620]">Bugünkü en yüksek 30 ({overlap.date})</CardTitle>
            <p className="text-xs text-[#7A6A5C]">İki modelin ne kadar farklı düşündüğünü gösterir. Listeler neredeyse aynıysa karşılaştırmanın anlamı azalır.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <SymbolChips title="İkisi de seçti" items={overlap.both} tone="border-[#6D7C3B]/30 bg-[#6D7C3B]/10 text-[#6D7C3B]" />
            <SymbolChips title="Yalnızca ana model" items={overlap.only_champion} tone="border-[#E6DCCF] bg-white text-[#2E2620]" />
            <SymbolChips title="Yalnızca aday" items={overlap.only_candidate} tone="border-[#C86F4A]/30 bg-[#C86F4A]/10 text-[#C86F4A]" />
          </CardContent>
        </Card>
      )}

      {candidate && (
        <div className="flex gap-2 flex-wrap items-center">
          <Button onClick={() => decide('promote')} disabled={!canDecide || busy}
            className="bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white rounded-full">
            Adayı ana model yap
          </Button>
          <Button variant="outline" onClick={() => decide('reject')} disabled={busy}
            className="border-[#B04832]/40 text-[#B04832] rounded-full hover:bg-[#B04832]/10">
            Adayı kaldır
          </Button>
          {!canDecide && (
            <span className="text-xs text-[#7A6A5C]">Terfi en az {minWeeks} haftalık gerçek sonuçtan sonra açılır.</span>
          )}
        </div>
      )}

      <Card className="card-organic overflow-hidden">
        <CardHeader className="pb-2"><CardTitle className="text-base text-[#2E2620]">Model geçmişi</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium">Sürüm</th>
                  <th className="text-left px-4 py-2.5 font-medium">Eğitim</th>
                  <th className="text-left px-4 py-2.5 font-medium">Durum</th>
                  <th className="text-right px-4 py-2.5 font-medium">Doğrulama IC</th>
                  <th className="text-left px-4 py-2.5 font-medium">Karar</th>
                </tr>
              </thead>
              <tbody>
                {(history || []).map((h) => (
                  <tr key={h.version} className="border-b border-[#E6DCCF]">
                    <td className="px-4 py-2.5 font-medium text-[#2E2620] whitespace-nowrap">{h.version}</td>
                    <td className="px-4 py-2.5 text-[#7A6A5C] whitespace-nowrap">{new Date(h.trained_at).toLocaleDateString('tr-TR')}</td>
                    <td className="px-4 py-2.5"><Badge className={(ROLE_LABELS[h.role] || ROLE_LABELS.retired).cls}>{(ROLE_LABELS[h.role] || ROLE_LABELS.retired).text}</Badge></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#2E2620]">{ic(h.holdout_ic)}</td>
                    <td className="px-4 py-2.5 text-xs text-[#7A6A5C]">{h.decision || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const SimonsScorePage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('current');
  const [view, setView] = useState('top');
  const [expanded, setExpanded] = useState(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [track, setTrack] = useState(null);
  const [trackDate, setTrackDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/simons/list?limit=50`, { headers: getAuthHeader() });
      setData(res.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Simons skoru yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadTrack = async (date) => {
    setTrackLoading(true);
    try {
      const res = await axios.get(`${API_URL}/simons/track`, {
        params: { limit: 50, ...(date ? { date } : {}) },
        headers: getAuthHeader(),
      });
      setTrack(res.data || null);
      setTrackDate(res.data?.date || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Takip verisi yüklenemedi');
    } finally {
      setTrackLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'track' && !track) loadTrack();
    setExpanded(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openAnalysis = (symbol) => {
    const end = new Date();
    navigate('/analysis', {
      state: {
        symbol,
        startDate: format(subYears(end, 1), 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
        initialInterval: '1d',
      },
    });
  };

  const toggle = (symbol) => setExpanded((cur) => (cur === symbol ? null : symbol));

  const validation = data?.model?.validation || {};
  const currentRows = view === 'top' ? data?.top : data?.bottom;
  const trackRows = view === 'top' ? track?.top : track?.bottom;
  const trackHasReturns = Boolean(track?.through);
  const summary = view === 'top' ? track?.top_summary : track?.bottom_summary;

  const tabBtn = (key, label) => (
    <button
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
        tab === key ? 'bg-[#2E2620] text-white' : 'text-[#7A6A5C] hover:bg-[#E6DCCF]/60'
      }`}
    >
      {label}
    </button>
  );

  const viewButtons = (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => { setView('top'); setExpanded(null); }}
        className={view === 'top' ? 'bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white' : 'bg-white border border-[#E6DCCF] text-[#7A6A5C] hover:bg-[#F6F1EA]'}>
        En yüksek 50
      </Button>
      <Button size="sm" onClick={() => { setView('bottom'); setExpanded(null); }}
        className={view === 'bottom' ? 'bg-[#B04832] hover:bg-[#963B28] text-white' : 'bg-white border border-[#E6DCCF] text-[#7A6A5C] hover:bg-[#F6F1EA]'}>
        En düşük 30
      </Button>
    </div>
  );

  const scoreBadge = (row) => (
    <Badge className={view === 'top'
      ? 'bg-[#6D7C3B]/15 text-[#6D7C3B] border-[#6D7C3B]/30 font-semibold'
      : 'bg-[#B04832]/15 text-[#B04832] border-[#B04832]/30 font-semibold'}>
      %{Math.round(row.score_pct)}
    </Badge>
  );

  const symbolCell = (row) => (
    <td className="px-4 py-2.5 font-semibold text-[#2E2620]">
      <span className="inline-flex items-center gap-1.5">
        {expanded === row.symbol
          ? <ChevronDown className="w-4 h-4 text-[#C86F4A]" />
          : <ChevronRight className="w-4 h-4 text-[#7A6A5C]" />}
        {row.symbol}
      </span>
    </td>
  );

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="simons-score-page">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-[#2E2620] font-['Playfair_Display'] flex items-center gap-3">
                Simons Skoru
                <Badge variant="outline" className="text-xs text-[#C86F4A] border-[#C86F4A]/30">Deneysel</Badge>
              </h1>
              <p className="text-[#7A6A5C] mt-2 max-w-3xl leading-relaxed">
                Mal toplama/dağıtma, geometri ve topoloji özelliklerini birlikte değerlendiren öğrenen model. Her likit
                hisseye, sonraki 1 ayda diğer hisselere göre ne kadar iyi gitmesinin beklendiğine göre bir sıra (yüzdelik)
                verir. Model her ay yeniden eğitilir, skorlar her gece güncellenir.
              </p>
            </div>
            <Button variant="outline" onClick={() => (tab === 'track' ? loadTrack(trackDate) : load())}
              disabled={loading || trackLoading} className="border-[#2E2620]/20 text-[#2E2620] rounded-full">
              {loading || trackLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Yenile
            </Button>
          </div>

          <div className="flex gap-1 bg-white border border-[#E6DCCF] rounded-full p-1 w-fit">
            {tabBtn('current', 'Güncel liste')}
            {tabBtn('track', 'Takip: tuttu mu?')}
            {tabBtn('compare', 'Ana model ve aday')}
          </div>

          {tab === 'compare' ? <ComparePanel /> : tab === 'current' ? (
            <>
              <Card className="card-organic">
                <CardContent className="pt-6 text-sm text-[#7A6A5C] leading-relaxed space-y-1">
                  <p>
                    <span className="font-medium text-[#2E2620]">Geriye dönük doğrulama:</span> model hiç görmediği 8 yılın
                    (2019–2026) 8'inde olumlu sonuç verdi; en yüksek %10, en düşük %10'dan sonraki ayda ortalama ~4 puan iyi gitti
                    ve diğer hisseleri %54 oranında geçti.
                  </p>
                  {data?.model && (
                    <p>
                      Model sürümü {data.model.version} · eğitim {new Date(data.model.trained_at).toLocaleDateString('tr-TR')}
                      {typeof validation.holdout_ic === 'number' ? ` · son 12 ay doğrulama IC ${validation.holdout_ic.toFixed(3)}` : ''}
                      {data.date ? ` · skor günü ${data.date} (${data.universe} hisse)` : ''}
                    </p>
                  )}
                  <p>Tek bir hisse için kesinlik değildir; küçük ama tutarlı avantaj çok sayıda hisseye yayıldığında anlam kazanır.</p>
                </CardContent>
              </Card>

              {viewButtons}

              <Card className="card-organic overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#2E2620]">
                    {view === 'top' ? 'Modelin en olumlu baktığı hisseler' : 'Modelin en olumsuz baktığı hisseler'}
                  </CardTitle>
                  <p className="text-xs text-[#7A6A5C]">Grafiği görmek için hisseye tıklayın.</p>
                </CardHeader>
                <CardContent className="p-0">
                  {loading && !data ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#C86F4A]" /></div>
                  ) : !data?.available ? (
                    <p className="px-6 py-6 text-sm text-[#7A6A5C]">Model henüz eğitilmedi. İlk eğitim gece görevinde yapılacak.</p>
                  ) : !currentRows?.length ? (
                    <p className="px-6 py-6 text-sm text-[#7A6A5C]">Henüz skor yok. Skorlar her gece hesaplanır.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                            <th className="text-left px-4 py-2.5 font-medium w-10">#</th>
                            <th className="text-left px-4 py-2.5 font-medium">Sembol</th>
                            <th className="text-left px-4 py-2.5 font-medium">Skor</th>
                            <th className="text-left px-4 py-2.5 font-medium">Mal toplama</th>
                            <th className="text-left px-4 py-2.5 font-medium">Geometri</th>
                            <th className="text-left px-4 py-2.5 font-medium">Topoloji</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentRows.map((row, idx) => (
                            <Fragment key={row.symbol}>
                              <tr onClick={() => toggle(row.symbol)}
                                className={`border-b border-[#E6DCCF] cursor-pointer hover:bg-[#F6F1EA] ${expanded === row.symbol ? 'bg-[#F6F1EA]' : ''}`}>
                                <td className="px-4 py-2.5 text-[#7A6A5C] tabular-nums">{idx + 1}</td>
                                {symbolCell(row)}
                                <td className="px-4 py-2.5">{scoreBadge(row)}</td>
                                <td className="px-4 py-2.5"><SubScore value={row.acc_pct} /></td>
                                <td className="px-4 py-2.5"><SubScore value={row.geo_pct} /></td>
                                <td className="px-4 py-2.5"><SubScore value={row.top_pct} /></td>
                              </tr>
                              {expanded === row.symbol && (
                                <tr><td colSpan={6} className="p-0">
                                  <StockDetail row={row} onOpenAnalysis={openAnalysis} />
                                </td></tr>
                              )}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="card-organic">
                <CardContent className="pt-6 text-sm text-[#7A6A5C] leading-relaxed space-y-3">
                  <p>
                    Her gece çıkan liste olduğu gibi kaydedilir, sonradan değiştirilemez. Burada geçmiş bir günün listesini seçip
                    o günden bugüne <span className="font-medium text-[#2E2620]">her hissenin gerçekte ne yaptığını</span> görürsünüz.
                    Kıyas, o gün skorlanan tüm hisselerin ortanca (medyan) getirisidir: "piyasaya göre" sütunu bu ortancanın
                    ne kadar üstünde ya da altında kaldığını gösterir.
                  </p>
                  {track?.dates?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#2E2620] font-medium">Liste günü:</span>
                      <select
                        value={trackDate}
                        onChange={(e) => { setExpanded(null); loadTrack(e.target.value); }}
                        className="border border-[#E6DCCF] rounded-lg px-3 py-1.5 bg-white text-[#2E2620]"
                      >
                        {track.dates.map((d) => (
                          <option key={d.date} value={d.date}>{d.date} ({d.universe} hisse)</option>
                        ))}
                      </select>
                      {trackHasReturns && (
                        <span>→ {track.through} ({track.trading_days} işlem günü)</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {trackLoading && !track ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#C86F4A]" /></div>
              ) : !track?.available || !track?.dates?.length ? (
                <Card className="card-organic"><CardContent className="pt-6 text-sm text-[#7A6A5C]">Henüz kayıtlı liste yok.</CardContent></Card>
              ) : (
                <>
                  {!trackHasReturns ? (
                    <Card className="card-organic">
                      <CardContent className="pt-6 text-sm text-[#7A6A5C] leading-relaxed">
                        Bu liste en son işlem gününe ait; üzerinden henüz gün geçmediği için sonuç yok. Takip her yeni işlem
                        günüyle dolar. Anlamlı bir değerlendirme için yaklaşık 1 ay (22 işlem günü) gerekir; daha kısa sürede
                        sonuçlar büyük ölçüde rastlantıdır.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Card className="card-organic"><CardContent className="pt-5">
                        <p className="text-xs text-[#7A6A5C]">{view === 'top' ? 'En yüksek 50' : 'En düşük 30'} · ortalama getiri</p>
                        <p className="text-2xl mt-1"><Pct value={summary?.avg_return_pct} bold /></p>
                      </CardContent></Card>
                      <Card className="card-organic"><CardContent className="pt-5">
                        <p className="text-xs text-[#7A6A5C]">Tüm hisseler · ortanca getiri</p>
                        <p className="text-2xl mt-1"><Pct value={track.median_all_pct} bold /></p>
                      </CardContent></Card>
                      <Card className="card-organic"><CardContent className="pt-5">
                        <p className="text-xs text-[#7A6A5C]">Ortancayı geçen oranı</p>
                        <p className="text-2xl mt-1 font-semibold text-[#2E2620] tabular-nums">
                          {summary ? `%${Math.round(summary.beat_rate)}` : '—'}
                        </p>
                        <p className="text-xs text-[#7A6A5C] mt-1">
                          {view === 'top' ? 'Yüksek olması iyi' : 'Düşük olması iyi'} · rastgele beklenti %50
                        </p>
                      </CardContent></Card>
                    </div>
                  )}

                  {viewButtons}

                  <Card className="card-organic overflow-hidden">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-[#2E2620]">
                        {trackDate} listesi · {view === 'top' ? 'en yüksek 50' : 'en düşük 30'}
                      </CardTitle>
                      <p className="text-xs text-[#7A6A5C]">Grafiği görmek için hisseye tıklayın.</p>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#E6DCCF] text-xs text-[#7A6A5C] uppercase tracking-wide">
                              <th className="text-left px-4 py-2.5 font-medium w-10">#</th>
                              <th className="text-left px-4 py-2.5 font-medium">Sembol</th>
                              <th className="text-left px-4 py-2.5 font-medium">O günkü skor</th>
                              <th className="text-right px-4 py-2.5 font-medium">O günden bugüne</th>
                              <th className="text-right px-4 py-2.5 font-medium">Piyasaya göre</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(trackRows || []).map((row) => (
                              <Fragment key={row.symbol}>
                                <tr onClick={() => toggle(row.symbol)}
                                  className={`border-b border-[#E6DCCF] cursor-pointer hover:bg-[#F6F1EA] ${expanded === row.symbol ? 'bg-[#F6F1EA]' : ''}`}>
                                  <td className="px-4 py-2.5 text-[#7A6A5C] tabular-nums">{row.rank}</td>
                                  {symbolCell(row)}
                                  <td className="px-4 py-2.5">{scoreBadge(row)}</td>
                                  <td className="px-4 py-2.5 text-right"><Pct value={row.return_pct} /></td>
                                  <td className="px-4 py-2.5 text-right"><Pct value={row.excess_pct} bold /></td>
                                </tr>
                                {expanded === row.symbol && (
                                  <tr><td colSpan={5} className="p-0">
                                    <StockDetail row={row} sinceDate={trackDate} onOpenAnalysis={openAnalysis} />
                                  </td></tr>
                                )}
                              </Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SimonsScorePage;
